import React, { useState, useEffect } from 'react';
import { Container, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getFirestore, collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { 
  FaLink, FaDollarSign, FaBrain, FaChartBar, FaBriefcase, 
  FaTools, FaUniversity, FaChartLine, FaRedo, FaClock 
} from 'react-icons/fa';
import NavbarComponent from '../../components/NavbarComponent';
import FooterComponent from '../../components/FooterComponent';
import styled from 'styled-components';

// Styled components for course items
const CourseCard = styled.div`
  display: flex;
  align-items: center;
  background-color: ${(props) => (props.selected ? '#2ECC71' : '#fff')}; 
  color: ${(props) => (props.selected ? '#fff' : '#000')}; 
  border-radius: 15px;
  padding: 15px;
  margin-bottom: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  transition: transform 0.2s ease-in-out;
  
  &:hover {
    transform: scale(1.03);
  }
`;

const IconWrapper = styled.div`
  font-size: 22px;
  margin-right: 15px;
`;

const CourseTitle = styled.span`
  flex: 1;
`;

// Mapping course names to icons
const courseIcons = {
  Crypto: <FaLink />,
  Forex: <FaDollarSign />,
  Psychology: <FaBrain />,
  Investing: <FaChartBar />,
  Trading: <FaBriefcase />,
  'Technical Analysis': <FaTools />,
  'Fundamental Analysis': <FaUniversity />,
  'Stock Trading': <FaChartLine />,
  Commodities: <FaRedo />,
  Futures: <FaClock />,
};

const CourseSelection = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const navigate = useNavigate();
  const db = getFirestore();
  const auth = getAuth();

  // Fetch courses from Firebase
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'courses_new'));
        const fetchedCourses = snapshot.docs.map((doc) => doc.data());
        setCourses(fetchedCourses);
      } catch (error) {
        console.error('Error fetching courses:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  // Handle Course Selection
  const handleCourseSelection = (course) => {
    setSelectedCourse(course);
  };

  // Handle Continue Button Click
  const handleContinue = async () => {
    if (!selectedCourse) {
      alert('Please select a course before continuing.');
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      alert('User not found. Please log in again.');
      return;
    }

    try {
      await addDoc(collection(db, 'usercoursebookings'), {
        active: "1",
        courseid: selectedCourse.courseid.toString(),
        coursename: selectedCourse.coursename,
        date: serverTimestamp(),
        userid: currentUser.uid,
      });

      console.log('Course selection saved successfully!');
      navigate('/find-course-skill-level', { state: { selectedCourse } });
    } catch (error) {
      console.error('Error saving course selection:', error);
      alert('Failed to save course selection. Please try again.');
    }
  };

  return (
    <>
      {/* ✅ Navbar */}
      <NavbarComponent />

      <Container className="text-center my-5">
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#2ECC71' }}>Courses</h2>

        {loading ? (
          <p>Loading...</p>
        ) : (
          courses.map((course) => (
            <CourseCard
              key={course.courseid}
              selected={selectedCourse?.courseid === course.courseid}
              onClick={() => handleCourseSelection(course)}
            >
              <IconWrapper>{courseIcons[course.coursename] || <FaChartLine />}</IconWrapper>
              <CourseTitle>{course.coursename}</CourseTitle>
            </CourseCard>
          ))
        )}

        {/* ✅ Continue Button */}
        <Button
          onClick={handleContinue}
          style={{
            backgroundColor: selectedCourse ? '#FF7C24' : '#ccc',
            borderColor: selectedCourse ? '#FF7C24' : '#ccc',
            fontSize: '18px',
            fontWeight: 'bold',
            borderRadius: '10px',
            padding: '12px 30px',
            marginTop: '20px',
            cursor: selectedCourse ? 'pointer' : 'not-allowed',
          }}
          disabled={!selectedCourse}
        >
          Continue
        </Button>
      </Container>

      {/* ✅ Footer */}
      <FooterComponent />
    </>
  );
};

export default CourseSelection;
